import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { Link } from 'react-router-dom';
import { FaSync, FaExclamationCircle, FaRobot, FaFolderOpen, FaInfoCircle } from 'react-icons/fa';
import FileList from '../Files/FileList';
import SearchBar from '../Files/SearchBar';
import { formatFileSize } from '../../utils/formatters';
import '../../styles/Dashboard.css';

const API = generateClient();

const Dashboard = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mmfConnected, setMmfConnected] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastSync, setLastSync] = useState(null);
  
  useEffect(() => {
    fetchUserData();
  }, []);
  
  const fetchUserData = async () => {
    setLoading(true);
    try {
      // DEVELOPMENT ONLY: Use mock data instead of API call
      // Comment this section out when backend is ready
      const mockUserData = {
        name: "Test User",
        mmfConnected: true,
        totalFiles: 42,
        categorizedFiles: 28,
        uncategorizedFiles: 14,
        storageUsed: 2147483648, // 2GB
        lastSync: new Date().toISOString()
      };
      
      setUserData(mockUserData);
      setMmfConnected(mockUserData.mmfConnected);
      
      if (mockUserData.lastSync) {
        setLastSync(new Date(mockUserData.lastSync));
      }
      setLoading(false);
      return;
      // END MOCK DATA SECTION
      
      // UNCOMMENT THIS WHEN BACKEND IS READY:
      // Get user profile data
      // const userResponse = await API.get('mmfBackupApi', '/user/profile', {});
      // setUserData(userResponse);
      // 
      // // Check if MMF is connected
      // if (userResponse.mmfConnected) {
      //   setMmfConnected(true);
      //   
      //   // Get last backup time if available
      //   if (userResponse.lastSync) {
      //     setLastSync(new Date(userResponse.lastSync));
      //   }
      // }
    } catch(err) {
      console.error("Error fetching user data:", err);
      setError('Failed to load user data');
      setLoading(false);
    }
  };

  const syncFiles = async () => {
    try {
      // In a real implementation, this would call the API to sync files from MMF
      // For now, we'll just update the lastSync time to simulate a successful sync
      
      // await API.post('mmfBackupApi', '/files/sync', {});
      
      setLastSync(new Date());
      alert('File synchronization initiated! New files will be available shortly.');
      
      // Refresh the user data to show updated stats
      setTimeout(fetchUserData, 2000);
    } catch (err) {
      console.error('Error syncing files:', err);
      alert('Failed to sync files: ' + err.message);
    }
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <FaSync className="fa-spin" />
        <p>Loading your dashboard...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <FaExclamationCircle />
        <h2>Error Loading Dashboard</h2>
        <p>{error}</p>
        <button onClick={fetchUserData} className="retry-button">Retry</button>
      </div>
    );
  }
  
  if (!mmfConnected) {
    return (
      <div className="not-connected-container">
        <h2>Welcome to MMF File Organizer</h2>
        <p>You need to connect your MyMiniFactory account to start organizing your files.</p>
        <div className="connection-steps">
          <h3>How it works:</h3>
          <ol>
            <li>Connect your MyMiniFactory account</li>
            <li>We'll fetch your files and prepare them for organization</li>
            <li>Use our AI tools to automatically organize your files</li>
            <li>Create custom folders and organize files your way</li>
          </ol>
        </div>
        <Link to="/auth/mmf-connect" className="connect-button">Connect to MMF</Link>
      </div>
    );
  }
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2>Welcome, {userData?.name || 'User'}</h2>
        {lastSync && (
          <p className="last-sync">
            Last synchronized: {lastSync.toLocaleString()}
          </p>
        )}
      </div>
      
      <div className="dashboard-stats">
        <div className="stat-card">
          <h3>Total Files</h3>
          <p className="stat-value">{userData?.totalFiles || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Categorized</h3>
          <p className="stat-value">{userData?.categorizedFiles || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Uncategorized</h3>
          <p className="stat-value">{userData?.uncategorizedFiles || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Storage Used</h3>
          <p className="stat-value">{formatFileSize(userData?.storageUsed || 0)}</p>
        </div>
      </div>
      
      <div className="dashboard-actions">
        <button onClick={syncFiles} className="primary-button">
          <FaSync /> Sync Files
        </button>
      </div>
      
      <div className="dashboard-features">
        <div className="feature-card">
          <div className="feature-icon">
            <FaFolderOpen />
          </div>
          <h3>Manual Organization</h3>
          <p>Create folders and organize your files using drag and drop.</p>
        </div>
        
        <div className="feature-card highlight">
          <div className="feature-icon">
            <FaRobot />
          </div>
          <h3>AI Organization</h3>
          <p>Let our AI organize your files automatically based on their content and type.</p>
        </div>
        
        <div className="feature-card">
          <div className="feature-icon">
            <FaInfoCircle />
          </div>
          <h3>File Metadata</h3>
          <p>View detailed information about your files, including dimensions and tags.</p>
        </div>
      </div>
      
      <SearchBar onSearch={setSearchTerm} />
      
      <FileList searchTerm={searchTerm} />
    </div>
  );
};

export default Dashboard;